import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, doc, setDoc, deleteDoc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import YapperModal from '../components/YapperModal';
import './AdminManagement.css';

function AdminManagement({ user }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workerEmail, setWorkerEmail] = useState('');
  const [workerLoading, setWorkerLoading] = useState(false);
  const [workerStatus, setWorkerStatus] = useState('');
  const [premiumEmail, setPremiumEmail] = useState('');
  const [premiumLoading, setPremiumLoading] = useState(false);
  const [removePremiumEmail, setRemovePremiumEmail] = useState('');
  const [removePremiumLoading, setRemovePremiumLoading] = useState(false);
  const [banInput, setBanInput] = useState('');
  const [banLoading, setBanLoading] = useState(false);
  const [unbanInput, setUnbanInput] = useState('');
  const [unbanLoading, setUnbanLoading] = useState(false);
  const [timeoutInput, setTimeoutInput] = useState('');
  const [timeoutLoading, setTimeoutLoading] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });
  const navigate = useNavigate();

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  useEffect(() => {
    checkStaffStatus();
  }, [user]);

  useEffect(() => {
    if (isStaff) {
      loadAdmins();
      loadWorkers();
    }
  }, [isStaff]);

  const checkStaffStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        navigate('/');
        return;
      }

      // Check if admin
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      if (adminDoc.exists()) {
        setIsAdmin(true);
        setIsWorker(false);
        setIsStaff(true);
        return;
      }

      // Check if worker
      const workerDoc = await getDoc(doc(db, 'workers', userId));
      if (workerDoc.exists()) {
        setIsAdmin(false);
        setIsWorker(true);
        setIsStaff(true);
        return;
      }

      // Neither admin nor worker
      setStatus('❌ Access Denied: You do not have permission.');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Error checking staff status:', error);
      setStatus('❌ Error checking permissions.');
    }
  };

  const loadAdmins = async () => {
    try {
      const adminsSnapshot = await getDocs(collection(db, 'admins'));
      const adminsList = [];

      for (const adminDoc of adminsSnapshot.docs) {
        const adminData = adminDoc.data();
        adminsList.push({
          id: adminDoc.id,
          email: adminData.email,
          username: adminData.username,
          addedAt: adminData.addedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'
        });
      }

      setAdmins(adminsList);
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  const loadWorkers = async () => {
    try {
      const workersSnapshot = await getDocs(collection(db, 'workers'));
      const workersList = [];

      for (const workerDoc of workersSnapshot.docs) {
        const workerData = workerDoc.data();
        workersList.push({
          id: workerDoc.id,
          email: workerData.email,
          username: workerData.username,
          addedAt: workerData.addedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'
        });
      }

      setWorkers(workersList);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };

  const addWorker = async () => {
    if (!workerEmail.trim()) {
      setWorkerStatus('⚠️ Please enter an email address.');
      return;
    }

    setWorkerLoading(true);
    setWorkerStatus('🔍 Searching for user...');

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', workerEmail.trim()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setWorkerStatus(`❌ No user found with email: ${workerEmail}`);
        setWorkerLoading(false);
        return;
      }

      let userId = null;
      let username = null;
      snapshot.forEach(userDoc => {
        userId = userDoc.id;
        username = userDoc.data().username || userDoc.data().nickname || 'User';
      });

      // Check if already a worker
      const existingWorker = await getDoc(doc(db, 'workers', userId));
      if (existingWorker.exists()) {
        setWorkerStatus(`⚠️ ${username} is already a worker.`);
        setWorkerLoading(false);
        return;
      }

      // Check if already an admin
      const existingAdmin = await getDoc(doc(db, 'admins', userId));
      if (existingAdmin.exists()) {
        setWorkerStatus(`⚠️ ${username} is already an admin. Admins don't need worker status.`);
        setWorkerLoading(false);
        return;
      }

      setWorkerStatus('✨ Adding worker...');

      await setDoc(doc(db, 'workers', userId), {
        email: workerEmail.trim(),
        username: username,
        addedAt: serverTimestamp(),
        addedBy: localStorage.getItem('userId'),
        permissions: ['view_reports', 'ban_users', 'resolve_reports']
      });

      setWorkerStatus(`🎉 SUCCESS! ${username} is now a worker!`);
      setWorkerEmail('');
      loadWorkers();
    } catch (error) {
      console.error('Error adding worker:', error);
      setWorkerStatus(`❌ Error: ${error.message}`);
    } finally {
      setWorkerLoading(false);
    }
  };

  const removeWorker = async (workerId, workerEmailAddr, workerUsername) => {
    showConfirm('Remove Worker', `Are you sure you want to remove ${workerUsername} (${workerEmailAddr}) as a worker?`, async () => {
      try {
        await deleteDoc(doc(db, 'workers', workerId));
        setWorkerStatus(`✅ ${workerUsername} has been removed as a worker.`);
        loadWorkers();
      } catch (error) {
        console.error('Error removing worker:', error);
        setWorkerStatus(`❌ Error removing worker: ${error.message}`);
      }
    });
  };

  const manualActivatePremium = async () => {
    if (!premiumEmail.trim()) {
      showAlert('Email Required', 'Please enter an email to activate premium for.', 'warning');
      return;
    }

    setPremiumLoading(true);
    try {
      // Find user by email (email lives in userPrivate; fall back to legacy users field)
      let targetId = null;
      const privSnapshot = await getDocs(query(collection(db, 'userPrivate'), where('email', '==', premiumEmail.trim())));
      if (!privSnapshot.empty) {
        targetId = privSnapshot.docs[0].id;
      } else {
        const legacySnapshot = await getDocs(query(collection(db, 'users'), where('email', '==', premiumEmail.trim())));
        if (!legacySnapshot.empty) targetId = legacySnapshot.docs[0].id;
      }

      let targetUser = null;
      if (targetId) {
        const userDoc = await getDoc(doc(db, 'users', targetId));
        if (userDoc.exists()) targetUser = { id: userDoc.id, ...userDoc.data() };
      }

      if (!targetUser) {
        showAlert('User Not Found', `No user found with email: ${premiumEmail}`, 'error');
        setPremiumLoading(false);
        return;
      }

      // Check if already premium
      if (targetUser.isPremium) {
        showAlert('Already Premium', `${targetUser.username} already has premium!`, 'info');
        setPremiumLoading(false);
        return;
      }

      // Activate premium
      await updateDoc(doc(db, 'users', targetUser.id), {
        isPremium: true,
        premiumActivatedAt: serverTimestamp(),
        premiumActivatedBy: localStorage.getItem('userId'),
        premiumActivatedManually: true
      });

      showAlert('Premium Activated', `Premium activated for ${targetUser.username || targetUser.email}!`, 'success');
      setPremiumEmail('');
    } catch (error) {
      console.error('Error activating premium:', error);
      showAlert('Error', `Failed to activate premium: ${error.message}`, 'error');
    } finally {
      setPremiumLoading(false);
    }
  };

  const manualRemovePremium = async () => {
    if (!removePremiumEmail.trim()) {
      showAlert('Email Required', 'Please enter an email to remove premium from.', 'warning');
      return;
    }

    setRemovePremiumLoading(true);
    try {
      // Find user by email (email lives in userPrivate; fall back to legacy users field)
      let targetId = null;
      const privSnapshot = await getDocs(query(collection(db, 'userPrivate'), where('email', '==', removePremiumEmail.trim())));
      if (!privSnapshot.empty) {
        targetId = privSnapshot.docs[0].id;
      } else {
        const legacySnapshot = await getDocs(query(collection(db, 'users'), where('email', '==', removePremiumEmail.trim())));
        if (!legacySnapshot.empty) targetId = legacySnapshot.docs[0].id;
      }

      let targetUser = null;
      if (targetId) {
        const userDoc = await getDoc(doc(db, 'users', targetId));
        if (userDoc.exists()) targetUser = { id: userDoc.id, ...userDoc.data() };
      }

      if (!targetUser) {
        showAlert('User Not Found', `No user found with email: ${removePremiumEmail}`, 'error');
        setRemovePremiumLoading(false);
        return;
      }

      // Check if has premium
      if (!targetUser.isPremium) {
        showAlert('No Premium', `${targetUser.username} does not have premium.`, 'info');
        setRemovePremiumLoading(false);
        return;
      }

      // Remove premium
      await updateDoc(doc(db, 'users', targetUser.id), {
        isPremium: false,
        premiumRemovedAt: serverTimestamp(),
        premiumRemovedBy: localStorage.getItem('userId')
      });

      showAlert('Premium Removed', `Premium removed from ${targetUser.username || targetUser.email}!`, 'success');
      setRemovePremiumEmail('');
    } catch (error) {
      console.error('Error removing premium:', error);
      showAlert('Error', `Failed to remove premium: ${error.message}`, 'error');
    } finally {
      setRemovePremiumLoading(false);
    }
  };

  // Find a user by username or email (email lives in userPrivate; fall back to legacy users field)
  const findUserByNameOrEmail = async (input) => {
    const term = input.trim();
    if (!term) return null;

    if (term.includes('@')) {
      let id = null;
      const priv = await getDocs(query(collection(db, 'userPrivate'), where('email', '==', term)));
      if (!priv.empty) {
        id = priv.docs[0].id;
      } else {
        const legacy = await getDocs(query(collection(db, 'users'), where('email', '==', term)));
        if (!legacy.empty) id = legacy.docs[0].id;
      }
      if (!id) return null;
      const uDoc = await getDoc(doc(db, 'users', id));
      return uDoc.exists() ? { id: uDoc.id, ...uDoc.data() } : null;
    }

    const byName = await getDocs(query(collection(db, 'users'), where('username', '==', term)));
    if (byName.empty) return null;
    const d = byName.docs[0];
    return { id: d.id, ...d.data() };
  };

  const handleBanAccount = async () => {
    if (!isAdmin) {
      showAlert('Admins Only', 'Only admins can ban accounts.', 'error');
      return;
    }
    if (!banInput.trim()) {
      showAlert('Input Required', 'Enter a username or email address to ban.', 'warning');
      return;
    }
    setBanLoading(true);
    try {
      const target = await findUserByNameOrEmail(banInput);
      const selfId = localStorage.getItem('userId') || user?.uid;
      if (!target) {
        showAlert('User Not Found', `No user found matching: ${banInput}`, 'error');
        return;
      }
      if (target.id === selfId) {
        showAlert('Cannot Ban Self', 'You cannot ban your own account.', 'error');
        return;
      }
      if (target.isBanned) {
        showAlert('Already Banned', `${target.username || target.id} is already banned.`, 'info');
        return;
      }
      showConfirm('Ban Account', `Ban ${target.username || target.id}? They will be blocked from using Yapp.`, async () => {
        try {
          await updateDoc(doc(db, 'users', target.id), {
            isBanned: true,
            isPermanentBan: true,
            bannedAt: serverTimestamp(),
            bannedBy: selfId || 'admin'
          });
          showAlert('Account Banned', `${target.username || target.id} has been banned.`, 'success');
          setBanInput('');
        } catch (err) {
          showAlert('Error', `Failed to ban account: ${err.message}`, 'error');
        }
      }, 'warning');
    } catch (error) {
      showAlert('Error', `Failed to ban account: ${error.message}`, 'error');
    } finally {
      setBanLoading(false);
    }
  };

  const handleUnbanAccount = async () => {
    if (!isAdmin) {
      showAlert('Admins Only', 'Only admins can unban accounts.', 'error');
      return;
    }
    if (!unbanInput.trim()) {
      showAlert('Input Required', 'Enter a username or email address to unban.', 'warning');
      return;
    }
    setUnbanLoading(true);
    try {
      const target = await findUserByNameOrEmail(unbanInput);
      if (!target) {
        showAlert('User Not Found', `No user found matching: ${unbanInput}`, 'error');
        return;
      }
      if (!target.isBanned) {
        showAlert('Not Banned', `${target.username || target.id} is not banned.`, 'info');
        return;
      }
      showConfirm('Unban Account', `Unban ${target.username || target.id}? They will regain access to Yapp.`, async () => {
        try {
          await updateDoc(doc(db, 'users', target.id), {
            isBanned: false,
            isPermanentBan: false,
            unbannedAt: serverTimestamp(),
            unbannedBy: localStorage.getItem('userId') || user?.uid || 'admin'
          });
          showAlert('Account Unbanned', `${target.username || target.id} has been unbanned.`, 'success');
          setUnbanInput('');
        } catch (err) {
          showAlert('Error', `Failed to unban account: ${err.message}`, 'error');
        }
      }, 'info');
    } catch (error) {
      showAlert('Error', `Failed to unban account: ${error.message}`, 'error');
    } finally {
      setUnbanLoading(false);
    }
  };

  const handleEndTimeout = async () => {
    if (!isAdmin) {
      showAlert('Admins Only', 'Only admins can end timeouts.', 'error');
      return;
    }
    if (!timeoutInput.trim()) {
      showAlert('Input Required', 'Enter a username or email address.', 'warning');
      return;
    }
    setTimeoutLoading(true);
    try {
      const target = await findUserByNameOrEmail(timeoutInput);
      if (!target) {
        showAlert('User Not Found', `No user found matching: ${timeoutInput}`, 'error');
        return;
      }
      if (!target.isTimedOut) {
        showAlert('Not Timed Out', `${target.username || target.id} is not currently timed out.`, 'info');
        return;
      }
      showConfirm('End Timeout', `End the timeout for ${target.username || target.id}? They will regain access immediately.`, async () => {
        try {
          await updateDoc(doc(db, 'users', target.id), {
            isTimedOut: false,
            timeoutUntil: null,
            timeoutEndedAt: serverTimestamp(),
            timeoutEndedBy: localStorage.getItem('userId') || user?.uid || 'admin'
          });
          showAlert('Timeout Ended', `${target.username || target.id}'s timeout has been lifted.`, 'success');
          setTimeoutInput('');
        } catch (err) {
          showAlert('Error', `Failed to end timeout: ${err.message}`, 'error');
        }
      }, 'info');
    } catch (error) {
      showAlert('Error', `Failed to end timeout: ${error.message}`, 'error');
    } finally {
      setTimeoutLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!isAdmin) {
      showAlert('Admins Only', 'Only admins can delete accounts.', 'error');
      return;
    }
    if (!deleteInput.trim()) {
      showAlert('Input Required', 'Enter a username or email address to delete.', 'warning');
      return;
    }
    setDeleteLoading(true);
    try {
      const target = await findUserByNameOrEmail(deleteInput);
      const selfId = localStorage.getItem('userId') || user?.uid;
      if (!target) {
        showAlert('User Not Found', `No user found matching: ${deleteInput}`, 'error');
        return;
      }
      if (target.id === selfId) {
        showAlert('Cannot Delete Self', 'You cannot delete your own account here.', 'error');
        return;
      }
      showConfirm('Delete Account', `Permanently delete ${target.username || target.id}'s account and profile data? This cannot be undone.`, async () => {
        try {
          await deleteDoc(doc(db, 'users', target.id));
          try { await deleteDoc(doc(db, 'userPrivate', target.id)); } catch (e) { /* may not exist */ }
          showAlert('Account Deleted', `${target.username || target.id}'s account data has been deleted.`, 'success');
          setDeleteInput('');
        } catch (err) {
          showAlert('Error', `Failed to delete account: ${err.message}`, 'error');
        }
      }, 'danger');
    } catch (error) {
      showAlert('Error', `Failed to delete account: ${error.message}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const addAdmin = async () => {
    if (!email.trim()) {
      setStatus('⚠️ Please enter an email address.');
      return;
    }

    setLoading(true);
    setStatus('🔍 Searching for user...');

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.trim()));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setStatus(`❌ No user found with email: ${email}`);
        setLoading(false);
        return;
      }

      let userId = null;
      let username = null;
      snapshot.forEach(userDoc => {
        userId = userDoc.id;
        username = userDoc.data().username || userDoc.data().nickname || 'User';
      });

      // Check if already admin
      const existingAdmin = await getDoc(doc(db, 'admins', userId));
      if (existingAdmin.exists()) {
        setStatus(`⚠️ ${username} is already an admin.`);
        setLoading(false);
        return;
      }

      setStatus('✨ Adding admin...');

      await setDoc(doc(db, 'admins', userId), {
        email: email.trim(),
        username: username,
        addedAt: serverTimestamp(),
        addedBy: localStorage.getItem('userId'),
        permissions: ['ban_users', 'view_reports', 'send_mass_messages']
      });

      setStatus(`🎉 SUCCESS! ${username} is now an admin!`);
      setEmail('');
      loadAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeAdmin = async (adminId, adminEmail, adminUsername) => {
    const currentUserId = localStorage.getItem('userId');

    if (adminId === currentUserId) {
      showAlert('Cannot Remove Self', 'You cannot remove yourself as an admin.', 'error');
      return;
    }

    showConfirm('Remove Admin', `Are you sure you want to remove ${adminUsername} (${adminEmail}) as an admin?`, async () => {
      try {
        await deleteDoc(doc(db, 'admins', adminId));
        setStatus(`✅ ${adminUsername} has been removed as an admin.`);
        loadAdmins();
      } catch (error) {
        console.error('Error removing admin:', error);
        setStatus(`❌ Error removing admin: ${error.message}`);
      }
    });
  };

  if (!isStaff) {
    return (
      <div className="admin-management-container">
        <div className="admin-management-card">
          <h2>⚠️ Access Denied</h2>
          <p>{status || 'Checking permissions...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-management-container">
      <div className="admin-management-card">
        <h1>{isAdmin ? '👑 Admin Management' : '🥈 Staff Panel'}</h1>
        <p className="admin-subtitle">{isAdmin ? 'Add or remove admin users' : 'View statistics and team info'}</p>

        {isAdmin && (
          <>
            <div className="add-admin-section">
              <h2>Add New Admin</h2>
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Enter user's email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="email-input"
                />
                <button
                  onClick={addAdmin}
                  disabled={loading}
                  className="add-admin-btn"
                >
                  {loading ? '⏳ Adding...' : '➕ Add Admin'}
                </button>
              </div>
              {status && <div className="status-message">{status}</div>}
            </div>

            <div className="admins-list-section">
              <h2>Current Admins ({admins.length})</h2>
              <div className="admins-grid">
                {admins.map(admin => (
                  <div key={admin.id} className="admin-item">
                    <div className="admin-info">
                      <div className="admin-header">
                        <span className="admin-star">⭐</span>
                        <span className="admin-username">{admin.username}</span>
                      </div>
                      <div className="admin-email">{admin.email}</div>
                      <div className="admin-date">Added: {admin.addedAt}</div>
                    </div>
                    {admin.id !== localStorage.getItem('userId') && (
                      <button
                        onClick={() => removeAdmin(admin.id, admin.email, admin.username)}
                        className="remove-admin-btn"
                        title="Remove admin"
                      >
                        ❌ Remove
                      </button>
                    )}
                    {admin.id === localStorage.getItem('userId') && (
                      <div className="current-user-badge">You</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="workers-management-section">
              <h2>🔧 Worker Management</h2>
              <p className="admin-subtitle">Workers can view and handle reports but cannot manage admins</p>

              <div className="add-admin-section">
                <h3>Add New Worker</h3>
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Enter user's email address"
                    value={workerEmail}
                    onChange={(e) => setWorkerEmail(e.target.value)}
                    disabled={workerLoading}
                    className="email-input"
                  />
                  <button
                    onClick={addWorker}
                    disabled={workerLoading}
                    className="add-admin-btn worker-btn"
                  >
                    {workerLoading ? '⏳ Adding...' : '➕ Add Worker'}
                  </button>
                </div>
                {workerStatus && <div className="status-message">{workerStatus}</div>}
              </div>

              <div className="admins-list-section">
                <h3>Current Workers ({workers.length})</h3>
                {workers.length === 0 ? (
                  <div className="no-workers">
                    <p>No workers yet. Add workers to help manage reports.</p>
                  </div>
                ) : (
                  <div className="admins-grid">
                    {workers.map(worker => (
                      <div key={worker.id} className="admin-item worker-item">
                        <div className="admin-info">
                          <div className="admin-header">
                            <span className="worker-star">🥈</span>
                            <span className="admin-username">{worker.username}</span>
                          </div>
                          <div className="admin-email">{worker.email}</div>
                          <div className="admin-date">Added: {worker.addedAt}</div>
                        </div>
                        <button
                          onClick={() => removeWorker(worker.id, worker.email, worker.username)}
                          className="remove-admin-btn"
                          title="Remove worker"
                        >
                          ❌ Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="premium-management-section">
              <h2>💎 Premium Management</h2>

              <div className="manual-premium-section">
                <h3>Manual Activation</h3>
                <p className="section-description">Manually activate premium for a user by email</p>
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Enter user's email address"
                    value={premiumEmail}
                    onChange={(e) => setPremiumEmail(e.target.value)}
                    disabled={premiumLoading}
                    className="email-input"
                  />
                  <button
                    onClick={manualActivatePremium}
                    disabled={premiumLoading}
                    className="add-admin-btn premium-btn"
                  >
                    {premiumLoading ? '⏳ Activating...' : '💎 Activate Premium'}
                  </button>
                </div>
              </div>

              <div className="manual-premium-section remove-premium-section">
                <h3>Remove Premium</h3>
                <p className="section-description">Remove premium from a user by email</p>
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Enter user's email address"
                    value={removePremiumEmail}
                    onChange={(e) => setRemovePremiumEmail(e.target.value)}
                    disabled={removePremiumLoading}
                    className="email-input"
                  />
                  <button
                    onClick={manualRemovePremium}
                    disabled={removePremiumLoading}
                    className="add-admin-btn remove-premium-btn"
                  >
                    {removePremiumLoading ? '⏳ Removing...' : '🚫 Remove Premium'}
                  </button>
                </div>
              </div>
            </div>

            <div className="admin-section premium-section">
              <h2>🛡️ Account Moderation</h2>

              <div className="manual-premium-section">
                <h3>Ban Account</h3>
                <p className="section-description">Ban a user by username or email address</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter username or email"
                    value={banInput}
                    onChange={(e) => setBanInput(e.target.value)}
                    disabled={banLoading}
                    className="email-input"
                  />
                  <button
                    onClick={handleBanAccount}
                    disabled={banLoading}
                    className="add-admin-btn remove-premium-btn"
                  >
                    {banLoading ? '⏳ Working...' : '🔨 Ban Account'}
                  </button>
                </div>
              </div>

              <div className="manual-premium-section">
                <h3>Unban Account</h3>
                <p className="section-description">Lift a ban from a user by username or email address</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter username or email"
                    value={unbanInput}
                    onChange={(e) => setUnbanInput(e.target.value)}
                    disabled={unbanLoading}
                    className="email-input"
                  />
                  <button
                    onClick={handleUnbanAccount}
                    disabled={unbanLoading}
                    className="add-admin-btn premium-btn"
                  >
                    {unbanLoading ? '⏳ Working...' : '✅ Unban Account'}
                  </button>
                </div>
              </div>

              <div className="manual-premium-section">
                <h3>End Timeout</h3>
                <p className="section-description">Lift a temporary timeout from a user by username or email address</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter username or email"
                    value={timeoutInput}
                    onChange={(e) => setTimeoutInput(e.target.value)}
                    disabled={timeoutLoading}
                    className="email-input"
                  />
                  <button
                    onClick={handleEndTimeout}
                    disabled={timeoutLoading}
                    className="add-admin-btn premium-btn"
                  >
                    {timeoutLoading ? '⏳ Working...' : '⏱️ End Timeout'}
                  </button>
                </div>
              </div>

              <div className="manual-premium-section remove-premium-section">
                <h3>Delete Account</h3>
                <p className="section-description">Permanently delete a user's account by username or email (cannot be undone)</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Enter username or email"
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    disabled={deleteLoading}
                    className="email-input"
                  />
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading}
                    className="add-admin-btn remove-premium-btn"
                  >
                    {deleteLoading ? '⏳ Working...' : '🗑️ Delete Account'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {isWorker && (
          <div className="worker-info-section">
            <p className="worker-notice">As a worker, you can view statistics and handle reports. Admin management features are restricted to admins only.</p>
          </div>
        )}

        <div className="stats-section">
          <h2>📊 Statistics</h2>
          <p className="section-description">View anonymous message statistics by country</p>
          <button
            onClick={() => navigate('/admin-stats')}
            className="add-admin-btn stats-btn"
          >
            📊 View Stats
          </button>
        </div>
      </div>

      <YapperModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
      />
    </div>
  );
}

export default AdminManagement;
